/**
 * ImageGallery - Displays generated images in a grid with selection and actions
 */

import React from "react";

import {
  Image as ImageIcon,
  Download,
  RefreshCw,
  Sparkles,
  Wand2,
  AlertCircle,
  Pencil,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { CopyableCell } from "@/components/ui/copyable-cell";
import { cn } from "@/lib/utils";
import { ImageGenRequestItem } from "@/types/image-generate-panel.types";

interface ImageGalleryProps {
  images: ImageGenRequestItem[];
  selectedAssets: Set<string>;
  isLoading: boolean;
  onImageClick: (img: ImageGenRequestItem) => void;
  onEditImage: (img: ImageGenRequestItem) => void;
}

/**
 * Strip token query parameters from URL for clean image display
 * Removes ?tkn=... or &tkn=... from URLs
 */
function stripTokenFromUrl(url: string | undefined): string | undefined {
  if (!url) return url;
  try {
    const urlObj = new URL(url);
    urlObj.searchParams.delete("tkn");
    urlObj.searchParams.delete("token");
    return urlObj.toString();
  } catch {
    // If URL parsing fails, try simple regex replacement
    return url.replace(/[?&]tkn=[^&]+/, "").replace(/[?&]token=[^&]+/, "");
  }
}

export function ImageGallery({
  images,
  selectedAssets,
  isLoading,
  onImageClick,
  onEditImage,
}: ImageGalleryProps): React.ReactElement {
  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <RefreshCw className="w-6 h-6 text-muted-foreground/50 animate-spin mb-3" />
        <p className="text-sm text-muted-foreground">Loading images...</p>
      </div>
    );
  }

  if (images.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <div className="w-12 h-12 rounded-xl bg-muted/50 flex items-center justify-center mb-4">
          <ImageIcon className="w-6 h-6 text-muted-foreground/50" />
        </div>
        <h3 className="text-sm font-medium text-foreground mb-1">
          No images yet
        </h3>
        <p className="text-xs text-muted-foreground max-w-[200px]">
          Generated images will appear here
        </p>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center gap-1.5 mb-2">
        <Sparkles className="w-3 h-3 text-purple-500" />
        <h4 className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider">
          AI Generated
        </h4>
        <span className="text-[10px] text-muted-foreground/60">
          ({images.length})
        </span>
      </div>
      <div className="grid grid-cols-3 gap-1.5">
        {images.map((img) => (
          <div
            key={img.id}
            className={cn(
              "group relative rounded-md overflow-hidden cursor-pointer",
              "bg-muted/30 border border-transparent",
              "hover:border-border hover:shadow-sm",
              "transition-all duration-150",
              selectedAssets.has(img.id) &&
                "ring-1 ring-primary ring-offset-1 ring-offset-background"
            )}
            onClick={() => onImageClick(img)}
          >
            <div className="aspect-square relative">
              {img.status === "completed" && img.downloadUrl ? (
                <img
                  src={stripTokenFromUrl(img.downloadUrl)}
                  alt={img.expectedFilename}
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              ) : img.status === "failed" ? (
                <div className="w-full h-full flex items-center justify-center bg-destructive/5">
                  <AlertCircle className="w-4 h-4 text-destructive/40" />
                </div>
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-purple-500/5 via-pink-500/5 to-blue-500/5">
                  <Wand2 className="w-4 h-4 text-purple-500/60 animate-pulse" />
                </div>
              )}

              {/* Hover overlay with actions */}
              {img.status === "completed" && img.downloadUrl && (
                <div className="absolute inset-0 bg-black/70 opacity-0 group-hover:opacity-100 transition-opacity duration-150 flex items-center justify-center gap-1.5">
                  <Button
                    variant="secondary"
                    size="sm"
                    className="h-7 w-7 p-0 bg-background/90 hover:bg-background border border-border/50 text-foreground"
                    onClick={(e) => {
                      e.stopPropagation();
                      onEditImage(img);
                    }}
                    title="Edit image"
                  >
                    <Pencil className="w-3.5 h-3.5" />
                  </Button>
                  <div onClick={(e) => e.stopPropagation()}>
                    <CopyableCell
                      value={stripTokenFromUrl(img.downloadUrl) || ""}
                      display={null}
                      tooltipLabel="Copy URL"
                      iconSize={14}
                      iconClassName="text-foreground"
                      buttonClassName="bg-background/90 hover:bg-background border border-border/50"
                    />
                  </div>
                  <Button
                    variant="secondary"
                    size="sm"
                    className="h-7 w-7 p-0 bg-background/90 hover:bg-background border border-border/50 text-foreground"
                    onClick={(e) => {
                      e.stopPropagation();
                      window.open(stripTokenFromUrl(img.downloadUrl), "_blank");
                    }}
                    title="Download"
                  >
                    <Download className="w-3.5 h-3.5" />
                  </Button>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
